import { Link } from "react-router-dom";
import { useCart } from "@/context/Providers";
import { hasPriceRange, money } from "@/lib/api";

const STRAIN = {
  Sativa: "border-jade/30 bg-jade/15 text-jade-soft",
  Indica: "border-purple-400/30 bg-purple-500/15 text-purple-300",
  Hybrid: "border-amber-400/30 bg-amber-500/15 text-amber-300",
};

export const StrainBadge = ({ strain }) =>
  strain ? <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STRAIN[strain] || "border-line text-gray-300"}`}>{strain}</span> : null;

export const ProductCard = ({ p, eager = false }) => {
  const { add } = useCart();
  const url = `/shop/product/${p.slug}/`;
  const cat = p.categories[0]?.name;
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-panel transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold/50" data-testid={`product-card-${p.slug}`}>
      <Link to={url} className="relative block aspect-square overflow-hidden bg-white" tabIndex={-1} aria-hidden>
        {p.thumb && <img src={p.thumb} alt="" width="300" height="300" loading={eager ? "eager" : "lazy"} decoding="async"
          className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105" />}
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {p.on_sale && <span className="rounded-full bg-gold px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-ink">Save {p.discount_pct}%</span>}
          {!p.in_stock && <span className="rounded-full bg-ink/85 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-200">Sold out</span>}
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <span>{cat}</span>{p.brand && <span>· {p.brand}</span>}<StrainBadge strain={p.strain} />
        </div>
        <h3 className="mt-2 font-sans text-base font-semibold leading-snug text-white">
          <Link to={url} className="hover:text-gold" data-testid={`product-card-link-${p.slug}`}>{p.name}</Link>
        </h3>
        <div className="mt-auto pt-4">
          <p className="flex items-baseline gap-2">
            {hasPriceRange(p) && <span className="text-xs text-gray-400">From</span>}
            <span className="text-lg font-bold text-white" data-testid={`product-card-price-${p.slug}`}>{money(p.price)}</span>
            {p.compare_price && <s className="text-sm text-gray-500">{money(p.compare_price)}</s>}
          </p>
          <p className={`mt-1 flex items-center gap-1.5 text-xs ${p.in_stock ? "text-jade-soft" : "text-gray-500"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${p.in_stock ? "bg-jade" : "bg-gray-600"}`} aria-hidden />{p.in_stock ? "In stock" : "Out of stock"}
          </p>
          {p.has_options || !p.in_stock ? (
            <Link to={url} className="btn-ghost mt-4 w-full min-h-[44px] whitespace-nowrap px-2 text-[11px] tracking-[0.04em] sm:px-6 sm:text-sm sm:tracking-[0.12em]" data-testid={`product-card-view-${p.slug}`}>{p.in_stock ? "Choose options" : "View product"}</Link>
          ) : (
            <button type="button" onClick={() => add(p, {}, 1)} className="btn-jade mt-4 w-full min-h-[44px] whitespace-nowrap px-2 text-[11px] tracking-[0.04em] sm:px-6 sm:text-sm sm:tracking-[0.12em]" data-testid={`product-card-add-${p.slug}`}>Add to cart</button>
          )}
        </div>
      </div>
    </article>
  );
};

export const ProductGrid = ({ items = [], eagerCount = 0 }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4" data-testid="product-grid">
    {items.map((p, i) => <ProductCard key={p.id} p={p} eager={i < eagerCount} />)}
  </div>
);

export const GridSkeleton = ({ n = 8 }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4" aria-hidden>
    {Array.from({ length: n }).map((_, i) => <div key={i} className="aspect-[3/5] animate-pulse rounded-2xl bg-panel" />)}
  </div>
);
